import * as React from 'react';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { isEqual } from 'lodash';
import { logDifference } from 'utils/helper';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton, TextField } from '@fluentui/react';
import { formStyles } from 'styles/formStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { BetreuerWorkMonth, updateBetreuerWorkMonth, useBetreuerWorkMonthOne } from 'api/betreuerWorkMonth';
import { hoursToTimeString, timeStringToHours } from 'utils/time';

const styles = mergeStyleSets({
    grid: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridColumnGap: 10,
        gridRowGap: 5,
        // minHeight: 200,
    },
});

interface BetreuerWorkMonthEditProps {
    betrNr: number;
    workYear: number;
    workMonth: number;
    onClose: (refetch: boolean) => void;
}

export const BetreuerWorkMonthEdit: React.FC<BetreuerWorkMonthEditProps> = ({
    betrNr,
    workYear,
    workMonth,
    onClose,
}) => {
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const betreuerWorkMonthOne = useBetreuerWorkMonthOne(
        {
            betrNr,
            workYear,
            workMonth,
        },
        {},
    );

    const [betreuerWorkMonth, setBetreuerWorkMonth] = React.useState<BetreuerWorkMonth | null>(null);

    React.useEffect(() => {
        if (!betreuerWorkMonthOne.isLoading && !betreuerWorkMonthOne.isFetching && betreuerWorkMonth === null) {
            if (betreuerWorkMonthOne.data) {
                setBetreuerWorkMonth({
                    ...betreuerWorkMonthOne.data,
                    holidayHoursPriorS: hoursToTimeString(betreuerWorkMonthOne.data.holidayHoursPrior, true) || '',
                    holidayHoursNewS: hoursToTimeString(betreuerWorkMonthOne.data.holidayHoursNew, true) || '',
                    holidayHoursCurrentS: hoursToTimeString(betreuerWorkMonthOne.data.holidayHoursCurrent, true) || '',
                    overtimeHoursPriorS: hoursToTimeString(betreuerWorkMonthOne.data.overtimeHoursPrior, true) || '',
                    overtimeHoursNewS: hoursToTimeString(betreuerWorkMonthOne.data.overtimeHoursNew, true) || '',
                    overtimeHoursCurrentS:
                        hoursToTimeString(betreuerWorkMonthOne.data.overtimeHoursCurrent, true) || '',
                });
                setSavingState(SavingState.notChanged);
            }
        }
    }, [betreuerWorkMonth, betreuerWorkMonthOne]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !betreuerWorkMonthOne.isLoading &&
            !betreuerWorkMonthOne.isFetching &&
            betreuerWorkMonthOne.data &&
            betreuerWorkMonth !== null
        ) {
            if (!isEqual(betreuerWorkMonthOne.data, betreuerWorkMonth)) {
                setSavingState(SavingState.notSaved);
                logDifference('betreuerWorkMonth', betreuerWorkMonth, betreuerWorkMonthOne.data);
            }
        }
    }, [savingState, betreuerWorkMonthOne, betreuerWorkMonth]);

    const validate = React.useCallback((): boolean => {
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [validationError]);

    const save = React.useCallback(() => {
        if (!betreuerWorkMonth) {
            return;
        }

        if (savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                updateBetreuerWorkMonth(betreuerWorkMonth)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }

                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    }, [betreuerWorkMonth, isMounted, onClose, savingState, validate]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    /*
    const renderDecimalField = (
        column: keyof BetreuerWorkMonth,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(betreuerWorkMonth?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (betreuerWorkMonth) {
                    setBetreuerWorkMonth({
                        ...betreuerWorkMonth,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );
     */

    const renderTime = (column: keyof BetreuerWorkMonth, label: string, options: { disabled?: boolean } = {}) => {
        let columnS:
            | 'holidayHoursPriorS'
            | 'holidayHoursNewS'
            | 'holidayHoursCurrentS'
            | 'overtimeHoursPriorS'
            | 'overtimeHoursNewS'
            | 'overtimeHoursCurrentS';

        switch (column) {
            case 'holidayHoursPrior':
                columnS = 'holidayHoursPriorS';
                break;
            case 'holidayHoursNew':
                columnS = 'holidayHoursNewS';
                break;
            case 'holidayHoursCurrent':
                columnS = 'holidayHoursCurrentS';
                break;
            case 'overtimeHoursPrior':
                columnS = 'overtimeHoursPriorS';
                break;
            case 'overtimeHoursNew':
                columnS = 'overtimeHoursNewS';
                break;
            case 'overtimeHoursCurrent':
                columnS = 'overtimeHoursCurrentS';
                break;
            default:
                throw new Error('unexpected column ' + column);
        }

        return (
            <TextField
                label={label}
                value={betreuerWorkMonth?.[columnS] || ''}
                disabled={savingState === SavingState.saving || options?.disabled}
                onChange={(event, newValue): void => {
                    let cleanedNewValue = (newValue ?? '').replaceAll(/[^0-9:]/g, '');
                    if (newValue?.startsWith('-')) {
                        cleanedNewValue = '-' + cleanedNewValue;
                    }

                    if (betreuerWorkMonth) {
                        setBetreuerWorkMonth({
                            ...betreuerWorkMonth,
                            [columnS]: cleanedNewValue,
                        });
                    }
                }}
                onBlur={() => {
                    if (betreuerWorkMonth) {
                        const valueS = (betreuerWorkMonth?.[columnS] || '').trim();
                        const value = valueS === '' ? null : timeStringToHours(valueS, true, true) || 0;

                        const newValueS = hoursToTimeString(value, true);

                        setBetreuerWorkMonth({
                            ...betreuerWorkMonth,
                            [column]: value,
                            [columnS]: newValueS,
                        });
                    }
                }}
            />
        );
    };

    const renderTextField = (column: keyof BetreuerWorkMonth, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(betreuerWorkMonth?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (betreuerWorkMonth) {
                    setBetreuerWorkMonth({
                        ...betreuerWorkMonth,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={workYear.toString() + '-' + workMonth.toString().padStart(2, '0')}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="90%"
                maxWidth="90%"
                onDismiss={() => {
                    onClose(false);
                }}
            >
                <form className={formStyles.form}>
                    <div className={styles.grid}>
                        <div>{renderTime('holidayHoursPrior', 'Urlaub Vormonat', { disabled: true })}</div>
                        <div>{renderTime('holidayHoursNew', 'Urlaub Anpassung')}</div>
                        <div>{renderTime('holidayHoursCurrent', 'Urlaub Aktuell', { disabled: true })}</div>
                        <div>{renderTime('overtimeHoursPrior', 'Überstunden Vormonat', { disabled: true })}</div>
                        <div>{renderTime('overtimeHoursNew', 'Überstunden Neu')}</div>
                        <div>{renderTime('overtimeHoursCurrent', 'Überstunden Aktuell', { disabled: true })}</div>
                        <div>{renderTextField('note', 'Anmerkung')}</div>
                    </div>

                    <DialogFooter>
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || betreuerWorkMonthOne.isLoading}
                            />
                        )}
                    </DialogFooter>

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                </form>
                {(betreuerWorkMonthOne.isLoading || betreuerWorkMonthOne.isFetching || !betreuerWorkMonth) && (
                    <LoadingIndicator />
                )}
            </Dialog>
        </>
    );
};
